import React, { createContext, useContext } from 'react';
import { Alert } from 'antd';
import { useLocation } from 'react-router-dom';
import './passenger-guidance.less';

type Passenger = {_id: string; personID?: string | {_id: string}};
export const HighlightedPassengers = createContext<ReadonlySet<string>>(new Set());

/** Resolve record IDs rather than names, which may be shared by different people. */
export function getHighlightedPassengerIDs(passengers: Passenger[], personID: string): Set<string> {
    return new Set((passengers || []).filter(passenger => {
        const id = typeof passenger.personID === 'string' ? passenger.personID : passenger.personID?._id;
        return !!personID && id === personID;
    }).map(passenger => passenger._id));
}

type Props = {
    flight: {paxIDList?: Passenger[]; state?: string; liveEditedState?: string};
    canEdit: boolean;
    editPermissionGranted: boolean;
    children: React.ReactNode;
};

/** Uses the editor's editability decision; state policies remain in its configuration. */
export default function FlightPassengerGuidance({flight, canEdit, editPermissionGranted, children}: Props) {
    const {search} = useLocation();
    const query = new URLSearchParams(search);
    const personID = query.get('highlightPersonID');
    const showGuidance = query.get('guidance') === 'remove-passenger' && !!personID;
    const passengerIDs = getHighlightedPassengerIDs(flight.paxIDList, showGuidance ? personID : null);

    return <HighlightedPassengers.Provider value={passengerIDs}>
        {showGuidance && <div className="mc-flight-passenger-guidance">
            <Alert
                type="info"
                showIcon
                message="Remove the passenger from this flight before deleting the person"
                description={passengerIDs.size ? <ol>
                    <li>Open a highlighted leg and find the highlighted passenger name.</li>
                    <li>Select only that person's passenger records, then click <strong>Remove Selected</strong> to remove them from the flight and its legs.</li>
                    <li>Return to the person deletion dialog and refresh the preview.</li>
                </ol> : 'This person is no longer listed on this flight. Return to the person deletion dialog and refresh the preview.'}
            />
            {!canEdit && <Alert
                type="warning"
                showIcon
                message="This flight cannot be edited here"
                description={<>
                    <p>Flight state: <strong>{flight.state || 'Unavailable'}</strong>.
                        {flight.liveEditedState && <> Live editing state: <strong>{flight.liveEditedState}</strong>.</>}</p>
                    {!editPermissionGranted && <p>You do not have permission to edit this flight.</p>}
                    <p>The passenger cannot be removed here while editing is unavailable.</p>
                </>}
            />}
        </div>}
        {children}
    </HighlightedPassengers.Provider>;
}

export function FlightPassengerHighlight({passengerID, children}: {passengerID: string; children: React.ReactNode}) {
    const passengerIDs = useContext(HighlightedPassengers);
    return passengerIDs.has(passengerID)
        ? <mark className="mc-pulse-highlight" title="Target passenger">{children}</mark>
        : <>{children}</>;
}
